import {
  Box,
  Grid,
  Typography,
  TextField,
  IconButton
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { Select } from "@aclymatepackages/atoms";
import {
  productCategories,
  productMaterials,
  productPackagingMaterials
} from "@aclymatepackages/lists";
import { batteryTypes } from "@aclymatepackages/products";

import NumberInput from "../../atoms/inputs/NumberInput";

const CATEGORY_OPTIONS = productCategories.map(({ id, label }) => ({
  value: id,
  label
}));

const MATERIAL_OPTIONS = [
  ...productMaterials.map(({ id, label }) => ({ value: id, label })),
  ...batteryTypes.map(({ type, label }) => ({ value: type, label })),
  { value: "electronics", label: "Electronics" }
];

const PACKAGING_MATERIAL_OPTIONS = productPackagingMaterials.map(
  ({ id, label }) => ({ value: id, label })
);

const TRANSPORTATION_METHOD_OPTIONS = [
  { value: "truck", label: "Truck" },
  { value: "rail", label: "Rail" },
  { value: "ocean", label: "Ocean" },
  { value: "air", label: "Air" }
];

const WEIGHT_UNIT_OPTIONS = [
  { value: "g", label: "grams" },
  { value: "%", label: "% of total" }
];

export const SectionHeader = ({ title, description, required }) => (
  <Box mb={1}>
    <Typography variant="subtitle2" color="textPrimary">
      {title}
      {required && (
        <Typography component="span" variant="caption" color="secondary">
          {" "}
          · required
        </Typography>
      )}
    </Typography>
    {description && (
      <Typography variant="caption" color="textSecondary">
        {description}
      </Typography>
    )}
  </Box>
);

export const CategorySection = ({
  category,
  subcategory,
  onChangeCategory,
  onChangeSubcategory,
  required,
  categoryReadOnly
}) => {
  const subcategoryOptions = (
    productCategories.find(({ id }) => id === category)?.subcategories || []
  ).map(({ id, label }) => ({ value: id, label }));
  const categoryLabel =
    CATEGORY_OPTIONS.find(({ value }) => value === category)?.label || category;
  return (
    <>
      <SectionHeader
        title="Category"
        description="Drives emissions classification and electronics defaults."
        required={required}
      />
      <Grid container spacing={1}>
        <Grid item xs={6}>
          {categoryReadOnly ? (
            <TextField
              fullWidth
              size="small"
              disabled
              label="Category"
              value={categoryLabel || ""}
            />
          ) : (
            <Select
              label="Category"
              value={category || ""}
              editValue={(value) => {
                onChangeCategory(value);
                onChangeSubcategory("");
              }}
              options={CATEGORY_OPTIONS}
            />
          )}
        </Grid>
        <Grid item xs={6}>
          <Select
            label="Subcategory"
            value={subcategory || ""}
            editValue={onChangeSubcategory}
            options={subcategoryOptions}
            disabled={!category}
          />
        </Grid>
      </Grid>
    </>
  );
};

const MaterialRow = ({ material, options, onChange, onRemove }) => (
  <Grid item container spacing={1} alignItems="center">
    <Grid item xs={5}>
      <Select
        label="Material"
        size="small"
        value={material.type || ""}
        editValue={(type) => onChange({ ...material, type })}
        options={options}
      />
    </Grid>
    <Grid item xs={3}>
      <NumberInput
        label="Weight"
        value={material.weight || ""}
        setValue={(next) => onChange({ ...material, weight: Number(next) || 0 })}
      />
    </Grid>
    <Grid item xs={3}>
      <Select
        label="Unit"
        size="small"
        value={material.weightUnit || "g"}
        editValue={(weightUnit) => onChange({ ...material, weightUnit })}
        options={WEIGHT_UNIT_OPTIONS}
      />
    </Grid>
    <Grid item xs={1}>
      <IconButton size="small" onClick={onRemove}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Grid>
  </Grid>
);

const MaterialsList = ({ materials, options, onChange }) => {
  const safeMaterials = materials || [];
  const updateMaterial = (idx, next) =>
    onChange(safeMaterials.map((m, i) => (i === idx ? next : m)));
  const removeMaterial = (idx) =>
    onChange(safeMaterials.filter((_, i) => i !== idx));
  const addMaterial = () =>
    onChange([...safeMaterials, { type: "", weight: 0, weightUnit: "g" }]);
  return (
    <Grid container direction="column" spacing={1}>
      {safeMaterials.map((material, idx) => (
        <MaterialRow
          key={`material-${idx}`}
          material={material}
          options={options}
          onChange={(next) => updateMaterial(idx, next)}
          onRemove={() => removeMaterial(idx)}
        />
      ))}
      <Grid item>
        <IconButton size="small" onClick={addMaterial}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export const FlatMaterialsSection = ({ materials, onChange }) => (
  <>
    <SectionHeader
      title="Materials"
      description="Main material components and their weights."
      required
    />
    <MaterialsList materials={materials} options={MATERIAL_OPTIONS} onChange={onChange} />
  </>
);

export const ComponentsSection = ({ components, onChange }) => {
  const safeComponents = components || [];
  const updateComponent = (idx, next) =>
    onChange(safeComponents.map((c, i) => (i === idx ? next : c)));
  const removeComponent = (idx) =>
    onChange(safeComponents.filter((_, i) => i !== idx));
  const addComponent = () =>
    onChange([...safeComponents, { name: "", productWeight: 0, materials: [] }]);
  return (
    <>
      <SectionHeader
        title="Components"
        description="Main parts of the product and the materials they're made from. Optional."
      />
      <Grid container direction="column" spacing={2}>
        {safeComponents.map((component, idx) => (
          <Grid
            item
            key={`component-${idx}`}
            style={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8 }}
            p={1.5}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={7}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Component name (e.g. Shell)"
                  value={component.name || ""}
                  onChange={(e) =>
                    updateComponent(idx, { ...component, name: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <NumberInput
                  label="Weight (g)"
                  value={component.productWeight || ""}
                  setValue={(next) =>
                    updateComponent(idx, {
                      ...component,
                      productWeight: Number(next) || 0
                    })
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton size="small" onClick={() => removeComponent(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
            <Box mt={1}>
              <MaterialsList
                materials={component.materials}
                options={MATERIAL_OPTIONS}
                onChange={(materials) =>
                  updateComponent(idx, { ...component, materials })
                }
              />
            </Box>
          </Grid>
        ))}
        <Grid item>
          <IconButton size="small" onClick={addComponent}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
    </>
  );
};

const ELECTRONICS_FIELDS = [
  { field: "utilizationTimes", label: "Charge cycles over product life" },
  { field: "capacityMAh", label: "Battery capacity (mAh)" },
  { field: "voltage", label: "Voltage (V)" },
  { field: "chargeEfficiencyPercentage", label: "Charge efficiency (%)" },
  { field: "dischargeEfficiencyPercentage", label: "Discharge efficiency (%)" },
  { field: "kwhConsumedDuringUse", label: "Energy consumed during use (kWh)" }
];

export const ElectronicsSection = ({ electronics, onChange }) => {
  const safeElectronics = electronics || {};
  return (
    <>
      <SectionHeader
        title="Electronics"
        description="Battery and usage specs for this electronic product. Optional."
      />
      <Grid container spacing={1}>
        {ELECTRONICS_FIELDS.map(({ field, label }) => (
          <Grid item xs={6} key={field}>
            <NumberInput
              label={label}
              value={safeElectronics[field] ?? ""}
              setValue={(next) =>
                onChange({
                  ...safeElectronics,
                  [field]: next === "" || next === null ? null : Number(next)
                })
              }
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export const PackagingSection = ({
  packaging,
  packagingWeight,
  onChange,
  onChangeWeight
}) => (
  <>
    <SectionHeader
      title="Packaging"
      description="Packaging materials and weights. Optional."
    />
    <Box mb={1} style={{ maxWidth: 200 }}>
      <NumberInput
        label="Total packaging weight (g)"
        value={packagingWeight || ""}
        setValue={(next) => onChangeWeight(Number(next) || 0)}
      />
    </Box>
    <MaterialsList
      materials={packaging}
      options={PACKAGING_MATERIAL_OPTIONS}
      onChange={onChange}
    />
  </>
);

const asDestinationString = (destination) => {
  if (!destination) {
    return "";
  }
  if (typeof destination === "string") {
    return destination;
  }
  const cityCountry = [destination.city, destination.country]
    .filter(Boolean)
    .join(", ");
  return (
    destination.address ||
    destination.name ||
    destination.label ||
    destination.description ||
    cityCountry ||
    ""
  );
};

const normalizeRoute = (route) => {
  if (!route) {
    return { origin: "", destination: "", mode: "" };
  }
  if (route.origin || route.destination || route.mode) {
    return {
      origin: route.origin || "",
      destination: route.destination || "",
      mode: route.mode || ""
    };
  }
  const destinations = route.destinations || [];
  const legs = route.legs || [];
  return {
    origin: asDestinationString(destinations[0]),
    destination: asDestinationString(destinations[destinations.length - 1]),
    mode: legs[0]?.transportationMethod || ""
  };
};

export const RouteSection = ({ route, onChange }) => {
  const { origin, destination, mode } = normalizeRoute(route);

  const updateField = (field, value) =>
    onChange({ origin, destination, mode, [field]: value });

  return (
    <>
      <SectionHeader
        title="Shipping route"
        description="Where the product is made and where it ships to. Optional."
      />
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Origin (city, country)"
            value={origin}
            onChange={(e) => updateField("origin", e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Destination (city, country)"
            value={destination}
            onChange={(e) => updateField("destination", e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <Select
            label="Transportation method"
            size="small"
            value={mode}
            editValue={(value) => updateField("mode", value)}
            options={TRANSPORTATION_METHOD_OPTIONS}
          />
        </Grid>
      </Grid>
    </>
  );
};
